
import { mapState, mapGetters } from 'vuex'
import tailwindConfig from '@/tailwind.config.js'

import ModalDialog from '@/components/ModalDialog'
import HeroButton from '@/components/HeroButton'
import EmptyBasket from '@/components/EmptyBasket'
import BasketGuesstimation from '@/components/BasketGuesstimation'
import BasketItem from '@/components/BasketItem'
import RecommendationCarousel from '@/components/RecommendationCarousel'
import Message from '@/components/Message'
import CheckoutLayout from '@/components/layouts/CheckoutLayout'
import PricingLayout from '@/components/layouts/PricingLayout'
import PromoCode from '@/components/PromoCode'
import ShippingThreshold from '@/components/ShippingThreshold'

export default {
  components: {
    ModalDialog,
    HeroButton,
    EmptyBasket,
    BasketItem,
    BasketGuesstimation,
    Message,
    CheckoutLayout,
    RecommendationCarousel,
    PromoCode,
    PricingLayout,
    ShippingThreshold,
  },
  data() {
    return {
      searchProduct: '',
      tablet: parseInt(tailwindConfig.theme.screens.tablet, 10),
      desktopMd: parseInt(tailwindConfig.theme.screens['desktop-md'], 10),
      itemToBeRemoved: null,
      showReorderError: this.$route.params.showReorderError || false,
      refreshShippingOptions: false,
      showShippingThresholdToLoggedOutCustomers: false,
      showEditPrescription: false,
      itemToEdit: null,
    }
  },
  computed: {
    ...mapState([
      'basket',
      'search',
      'loading',
      'errors',
      'maxQuantity',
      'couponCode',
      'couponDetails',
      'couponError',
      'defaultShippingMethod',
    ]),
    ...mapState('order', ['order', 'issues', 'shippingOptions', 'shippingFolder']),
    ...mapGetters('basket', ['totalPrice', 'totalSave']),
    shippingMethod() {
      if (!this.$auth.loggedIn) {
        return this.defaultShippingMethod
      }

      return this.shippingOptions.find((option) => option.folder === this.shippingFolder)
    },
    deliveryEstimate() {
      if (this.shippingMethod?.dates?.estimatedDelivery) {
        return { ...this.shippingMethod.dates.estimatedDelivery, cutoff: this.shippingMethod.dates.cutoff }
      } else {
        return {
          from: this.basket.deliveryDateFrom,
          to: this.basket.deliveryDateTo,
          cutoff: this.basket.cutoff,
        }
      }
    },
    discount() {
      return this.order.discount || this.basket.discount || 0
    },
    save() {
      return this.discount + this.totalSave
    },
    isBasketEmpty() {
      return this.basket.lines.length === 0
    },
    hasOrderIssues() {
      return this.issues.length !== 0
    },
    isLoggedIn() {
      return this.$auth.loggedIn
    },
    canShowShippingThreshold() {
      return !!(
        this.shippingMethod &&
        !this.shippingMethod.couponCodeFreeShipping &&
        this.shippingMethod.freeShippingAt !== null &&
        (this.$auth.loggedIn || this.showShippingThresholdToLoggedOutCustomers)
      )
    },
  },
  async mounted() {
    if (!this.isBasketEmpty && !this.hasOrderIssues) {
      if (this.$auth.loggedIn && !this.order?.orderId) {
        await this.$store.dispatch('order/createOrder')
      } else {
        await this.$store.dispatch('applyCouponCode')
      }
    }
    if (this.order.orderId) {
      await this.$store.dispatch('order/getShippingOptions')
    }

    this.showShippingThresholdToLoggedOutCustomers = await this.$store.dispatch(
      'split/getSplitTreatment',
      'showLoggedOutFreeShippingThreshold',
    )
    this.showEditPrescription = await this.$store.dispatch('split/getSplitTreatment', 'basketPageEditPrescription')
  },
  methods: {
    async removeCoupon() {
      await this.$store.dispatch('removeCouponCodeAndUpdateOrderOrBasket')
    },
    async applyCoupon(couponCode) {
      await this.$store.dispatch('applyCouponCode', couponCode)
    },
    goToCheckout() {
      if (this.$auth.loggedIn) {
        this.$router.push(this.$getLink('/delivery'))
      } else {
        this.$router.push(this.$getLink('/checkout'))
      }
    },
    goToHome() {
      this.$router.push('/')
    },
    confirmToRemove(item) {
      this.itemToBeRemoved = item
    },
    cancelToRemove() {
      this.itemToBeRemoved = null
    },
    async updateQuantity({ item, quantity }) {
      await this.$store.dispatch('basket/updateQuantity', { item, quantity })
      this.logQuantityChange(item, quantity)

      await this.updateShippingOptions()
    },
    editItemOptions(item) {
      this.itemToEdit = item
    },
    cancelEditItemOptions() {
      this.itemToEdit = null
    },
    async itemOptionsUpdated() {
      await this.updateShippingOptions()
    },
    updateShippingOptions() {
      clearTimeout(this.refreshShippingOptions)
      this.$store.commit('ADD_LOADING', 'shipping')

      this.$nextTick(() => {
        this.refreshShippingOptions = setTimeout(async () => {
          await this.$store.dispatch('order/getShippingOptions')
          this.$store.commit('REMOVE_LOADING', 'shipping')
        }, 750)
      })
    },
    async removeItem() {
      await this.$store.dispatch('basket/updateQuantity', { item: this.itemToBeRemoved, quantity: 0 })
      this.logLineItemRemoved()
      this.itemToBeRemoved = null

      await this.updateShippingOptions()
    },
    async addRecommendedToBasket(item) {
      await this.$store.dispatch('basket/addRecommendedToBasket', item)

      const { name, discountPrice, price, productCode, link, packSize, catName } = item

      const data = {
        event: 'addToCart',
        addToCartSource: 'basketPage',
        ecommerce: {
          currencyCode: 'GBP',
          add: {
            products: [
              {
                brand: '',
                category: '',
                currency: 'GBP',
                modality: '',
                packsize: packSize || '',
                id: productCode,
                name,
                price: discountPrice || price,
                quantity: 1,
                subcategory: catName || '',
                url: link,
                variant: '',
              },
            ],
          },
        },
      }

      this.$gtm.push({ ecommerce: null })
      this.$gtm.push(data)
    },
    logLineItemRemoved() {
      this.logItemQuantityDecrease(this.itemToBeRemoved, this.itemToBeRemoved.qty)
    },
    logQuantityChange(item, quantity) {
      const difference = quantity - item.qty
      if (difference > 0) {
        this.logItemQuantityIncrease(item, difference)
      } else if (difference < 0) {
        this.logItemQuantityDecrease(item, Math.abs(difference))
      }
    },
    logItemQuantityIncrease(item, quantity) {
      const { name, originalUnitPrice, unitPrice, code, link, packSize, catName } = item

      const data = {
        event: 'addToCart',
        ecommerce: {
          currencyCode: 'GBP',
          add: {
            products: [
              {
                brand: '',
                category: '',
                currency: 'GBP',
                modality: '',
                packsize: packSize || '',
                id: code,
                name,
                price: unitPrice || originalUnitPrice,
                quantity,
                subcategory: catName || '',
                url: link,
                variant: '',
              },
            ],
          },
        },
      }

      this.$gtm.push({ ecommerce: null })
      this.$gtm.push(data)
    },
    logItemQuantityDecrease(item, quantity) {
      const { name, originalUnitPrice, unitPrice, code, link, packSize, catName } = item
      const data = {
        event: 'removeFromCart',
        ecommerce: {
          currencyCode: 'GBP',
          remove: {
            products: [
              {
                brand: '',
                category: '',
                currency: 'GBP',
                modality: '',
                packsize: packSize || '',
                id: code,
                name,
                price: unitPrice || originalUnitPrice,
                quantity,
                subcategory: catName || '',
                url: link,
                variant: '',
              },
            ],
          },
        },
      }

      this.$gtm.push({ ecommerce: null })
      this.$gtm.push(data)
    },
  },
}
